
/**
 *
 * @description Utility functions for operations on the store
 */

import type {
  AdditionalPropertiesSchema,
  ApiDashboardElement,
  ApiDashboardParametersType,
  Collection,
  DashboardType,
  JSONSerializable,
  Map,
  ReportStructure,
  SourceMap,
  WidgetSlot
} from '@highimpact-frontend/api-types';
import type { DashboardLibraryState } from '@highimpact/dashboard-types';
import type { Layout } from 'react-grid-layout';
import type { Datasource } from '../dashboard/widget/widgets/map/mapTypes';
import * as R from '../internal/utils/record';
import {
  isConfiguredTableByCampaign,
  isConfiguredText,
} from './dashboard-element-operations';
import type { DashboardLayoutProperties } from './dashboard-layout-operations';
import {
  getOccupiedSlotRanks,
  hasAssignedWidgetSlot,
  isSvgConstrainedLayout,
  resolveSlotConflictsByGeometry,
  resolveWidgetSlotPlacement,
} from './dashboard-layout-operations';
import type {
  DashboardGridType,
  DashboardStoreType,
} from './dashboard/initials';
import {
  isDisplayer,
  makeWidgetRepresentation,
  type WidgetParamsFromName,
  type WidgetType,
} from './dashboard/widget';

// -- Dependency types

export interface ReportDashboardDependencies {
  reportStructure: ReportStructure;
  elementNameToVariableName: Record<string, string>;
  layouts: DashboardGridType['layouts']['lg'];
}

// -- Helpers

const ensureNumber = (n: number | string): number => {
  return typeof n === 'string' ? Number.parseInt(n) : n;
};

// -- Slices

export const getDashboardSlice = (store: DashboardLibraryState): DashboardStoreType => {
  return store.dashboard;
};

export const replaceDashboardSlice = (
  newDashboardSlice: DashboardStoreType,
  store: DashboardLibraryState
): DashboardLibraryState => {
  return {
    dashboard: newDashboardSlice,
  };
};

// -- Dashboard slice

type GridType = 'reports' | 'grids';

export const dashboardTypeToGridType = (
  dashboardType: DashboardType
): GridType => {
  switch (dashboardType) {
    case 'report_dashboard':
      return 'reports';
    case 'user_dashboard':
      return 'grids';
  }
};

export const getDashboardAtKey = (
  dashboardId: number,
  gridType: GridType,
  dashboardSlice: DashboardStoreType
): DashboardGridType | undefined => {
  return dashboardSlice[gridType][dashboardId];
};

export const getDashboards = (
  gridType: GridType,
  dashboardSlice: DashboardStoreType
): DashboardGridType[] => {
  return Object.values(dashboardSlice[gridType]);
};

export const getDashboardKeys = (
  gridType: GridType,
  dashboardSlice: DashboardStoreType
): number[] => {
  return Object.keys(dashboardSlice[gridType]).map((n) => Number.parseInt(n));
};

export const replaceDashboardAtKey = (
  dashboardId: number,
  gridType: GridType,
  newDashboard: DashboardGridType,
  DashboardSlice: DashboardStoreType
): DashboardStoreType => {
  return {
    ...DashboardSlice,
    [gridType]: {
      ...DashboardSlice[gridType],
      [dashboardId]: newDashboard,
    },
  };
};

export const updateAllDashboards = (
  gridType: GridType,
  update: (dashboard: DashboardGridType) => DashboardGridType,
  dashboardSlice: DashboardStoreType
): DashboardStoreType => {
  const dashboardKeys = getDashboardKeys(gridType, dashboardSlice);
  return dashboardKeys.reduce((acc, key) => {
    const dashboard = getDashboardAtKey(key, gridType, dashboardSlice);
    const updatedDashboard = update(dashboard!);
    return replaceDashboardAtKey(key, gridType, updatedDashboard, acc);
  }, dashboardSlice);
};

export const addDashboardAtKey = replaceDashboardAtKey;

export const addDashboard = (
  gridType: GridType,
  newDashboard: DashboardGridType,
  DashboardSlice: DashboardStoreType
): DashboardStoreType => {
  const dashboardKeys = getDashboardKeys(gridType, DashboardSlice);
  const key =
    dashboardKeys.length > 0
      ? Math.max(...getDashboardKeys(gridType, DashboardSlice)) + 1
      : 1;
  return addDashboardAtKey(key, gridType, newDashboard, DashboardSlice);
};

const normalizeAdditionalProperties = (
  additionalProperties: AdditionalPropertiesSchema | null | undefined
): DashboardLayoutProperties | undefined => {
  if (!additionalProperties) return undefined;

  if (isSvgConstrainedLayout(additionalProperties)) {
    return {
      layoutType: 'svg-constrained',
      widgetSlot: additionalProperties.widgetSlot,
      // FIXME: why is it optional?
      svgViewBox: additionalProperties.svgViewBox!,
      svgData: additionalProperties.svgData,
    };
  }

  return {
    layoutType: 'free',
    cols: additionalProperties.cols ?? undefined,
    rowHeight: additionalProperties.rowHeight ?? undefined,
    margin: (additionalProperties.margin?.length === 2
      ? additionalProperties.margin
      : undefined) as [number, number] | undefined,
  };
};

export const createEmptyDashboard = (
  name: string,
  publicUrl?: string,
  additionalProperties?: DashboardGridType['additionalProperties']
): DashboardGridType => {
  const fallback: DashboardLayoutProperties = {
    layoutType: 'free',
    cols: { lg: 24, md: 24, sm: 24, xs: 24, xxs: 24 },
    rowHeight: 32,
    margin: [12, 12],
  };

  const resolved = additionalProperties ?? fallback;

  return {
    name,
    publicUrl,
    additionalProperties: resolved,
    widgets: {},
    layouts: { lg: [] },
    params: {},
  };
};

export const setDashboardParams = (
  params: DashboardGridType['params'],
  dashboard: DashboardGridType
): DashboardGridType => {
  return {
    ...dashboard,
    params,
  };
};

export const removeDashboardAtKey = (
  dashboardId: number,
  gridType: GridType,
  DashboardSlice: DashboardStoreType
): DashboardStoreType => {
  const updatedGrid = R.filterWithKeys(
    (k, _) => Number.parseInt(`${k}`) !== dashboardId,
    DashboardSlice[gridType]
  );
  return {
    ...DashboardSlice,
    grids: updatedGrid,
  };
};

export const getWidgetKeyToDashboardElementIdMappingAtKey = (
  dashboardId: number,
  gridType: GridType,
  dashboardSlice: DashboardStoreType
): { [widgetId: string]: number } | undefined => {
  const dashboard = getDashboardAtKey(dashboardId, gridType, dashboardSlice);
  if (!dashboard) return undefined;
  return findElementIdsByWidgetKeys(dashboard);
};

export const findGridIdAndTypeByPredicate = (
  predicate: (dashboard: DashboardGridType) => boolean,
  dashboardSlice: DashboardStoreType
): { type: GridType; id: number } | undefined => {
  const resultInGrids = R.findKey(predicate, dashboardSlice.grids);
  if (resultInGrids !== undefined)
    return { type: 'grids', id: ensureNumber(resultInGrids) };
  const resultInReports = R.findKey(predicate, dashboardSlice.reports);
  if (resultInReports !== undefined)
    return { type: 'reports', id: ensureNumber(resultInReports) };
  return undefined;
};

export const findGridIdAndTypeByElementId = (
  elementId: number,
  dashboardSlice: DashboardStoreType
) => {
  const predicate = (dashboard: DashboardGridType) =>
    findWidgetByElementId(elementId, dashboard) !== undefined;
  return findGridIdAndTypeByPredicate(predicate, dashboardSlice);
};

// -- Dashboard grid

export const getWidgetAtKey = (
  widgetId: string,
  dashboard: DashboardGridType
): WidgetType | undefined => {
  return dashboard.widgets[widgetId];
};

export const getLayoutByWidgetId = (
  widgetId: string,
  dashboard: DashboardGridType
): Layout | undefined => {
  return dashboard.layouts['lg']!.find((l) => l.i === widgetId);
};

export const getWidgets = (dashboard: DashboardGridType): WidgetType[] => {
  return Object.values(dashboard.widgets);
};

export const getWidgetsByPredicate = (
  predicate: (widget: WidgetType) => boolean,
  dashboard: DashboardGridType
): WidgetType[] => {
  return getWidgets(dashboard).filter(predicate);
};

export const getLayouts = (dashboard: DashboardGridType): Layout[] => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return dashboard.layouts['lg']!;
};

export const getWidgetKeys = (dashboard: DashboardGridType): string[] => {
  return Object.keys(dashboard.widgets);
};

export const replaceWidgetAtKey = (
  widgetId: string,
  newWidget: WidgetType,
  dashboard: DashboardGridType
): DashboardGridType => {
  const updatedWidgets = { ...dashboard.widgets, [widgetId]: newWidget };
  return {
    ...dashboard,
    widgets: updatedWidgets,
  };
};

export const replaceLayoutByWidgetId = (
  widgetId: string,
  newLayout: Layout,
  dashboard: DashboardGridType
): DashboardGridType => {
  const updatedLayouts = dashboard.layouts['lg']!.map((l) =>
    l.i === widgetId ? newLayout : l
  );
  return {
    ...dashboard,
    layouts: { ...dashboard.layouts, lg: updatedLayouts },
  };
};

const getNextAvailableWidgetKey = (dashboard: DashboardGridType): string => {
  const widgetKeys = getWidgetKeys(dashboard);
  const keysAsNumbers = widgetKeys.map((k) =>
    Number.parseInt(k.replace(/\D/g, ''))
  );
  const number =
    keysAsNumbers.length === 0 ? 1 : Math.max(...keysAsNumbers) + 1;
  return `g${number}`;
};

export const addWidgetAndLayout = (
  newWidget: WidgetType,
  newLayout: Layout,
  dashboard: DashboardGridType
): DashboardGridType => {
  const widgetKey = newLayout.i;
  const updatedWidgets = { ...dashboard.widgets, [widgetKey]: newWidget };
  const updatedLayouts = [...dashboard.layouts['lg']!, newLayout];
  return {
    ...dashboard,
    widgets: updatedWidgets,
    layouts: { ...dashboard.layouts, lg: updatedLayouts },
  };
};

export const findWidgetByElementId = (
  elementId: number,
  dashboard: DashboardGridType
): WidgetType | undefined => {
  return R.find(
    (widget) => widget.dashboardElementId === elementId,
    dashboard.widgets
  );
};

export const findWidgetByElementIdInSlice = (
  elementId: number,
  gridType: GridType,
  dashboardSlice: DashboardStoreType
): WidgetType | undefined => {
  const dashboards = getDashboards(gridType, dashboardSlice);
  return dashboards
    .flatMap(getWidgets)
    .find((widget) => widget.dashboardElementId === elementId);
};

export const getWidgetInDashboard = (
  elementId: number,
  gridType: GridType,
  dashboardId: number,
  dashboardSlice: DashboardStoreType
): WidgetType | undefined => {
  const dashboard = getDashboardAtKey(dashboardId, gridType, dashboardSlice);
  if (dashboard === undefined) return undefined;
  const widgets = R.values(dashboard.widgets);
  return widgets.find((widget) => widget.dashboardElementId === elementId);
};

export const findWidgetByPredicate = (
  predicate: (widget: WidgetType) => boolean,
  dashboard: DashboardGridType
): WidgetType | undefined => {
  return R.find(predicate, dashboard.widgets);
};

export const findWidgetByPredicateInSlice = (
  predicate: (widget: WidgetType) => boolean,
  gridType: GridType,
  dashboardSlice: DashboardStoreType
): WidgetType | undefined => {
  const dashboards = getDashboards(gridType, dashboardSlice);
  return dashboards.flatMap(getWidgets).find(predicate);
};

export const findWidgetByPredicateAcrossGrids = (
  predicate: (widget: WidgetType) => boolean,
  dashboardSlice: DashboardStoreType
): WidgetType | undefined => {
  return (
    findWidgetByPredicateInSlice(predicate, 'grids', dashboardSlice) ??
    findWidgetByPredicateInSlice(predicate, 'reports', dashboardSlice)
  );
};

export const findWidgetByDisplayerIdAcrossGrids = (
  displayerId: number,
  dashboardSlice: DashboardStoreType
) => {
  return findWidgetByPredicateAcrossGrids(
    (widget) =>
      isDisplayer(widget) && widget.props.displayer_id === displayerId,
    dashboardSlice
  );
};

export const findDashboardTypeById = (
  dashboardId: number,
  dashboardSlice: DashboardStoreType
): 'grids' | 'reports' | undefined => {
  const gridDashboardIds = R.keys(dashboardSlice.grids).map(ensureNumber);
  if (gridDashboardIds.includes(dashboardId)) return 'grids';
  const reportDashboardIds = R.keys(dashboardSlice.reports).map(ensureNumber);
  if (reportDashboardIds.includes(dashboardId)) return 'reports';
  return undefined;
};

export const findWidgetKeyByElementId = (
  elementId: number,
  dashboard: DashboardGridType
): string | undefined => {
  return R.findKey(
    (widget) => widget.dashboardElementId === elementId,
    dashboard.widgets
  );
};

export const findElementIdByWidgetKey = (
  widgetKey: string,
  dashboard: DashboardGridType
): number | undefined => {
  return R.findWithKeys((id) => widgetKey === id, dashboard.widgets)
    ?.dashboardElementId;
};

export const findElementIdsByWidgetKeys = (
  dashboard: DashboardGridType
): { [widgetKey: string]: number } => {
  const pairs = R.entries(dashboard.widgets)
    .map(
      ([key, { dashboardElementId }]) =>
        [key, dashboardElementId] as [string, number | null]
    )
    .filter((tup): tup is [string, number] => tup[1] !== undefined);
  return R.fromArray(pairs);
};

export const findLayoutByElementId = (
  elementId: number,
  dashboard: DashboardGridType
): Layout | undefined => {
  const widgetKey = findWidgetKeyByElementId(elementId, dashboard);
  return widgetKey ? getLayoutByWidgetId(widgetKey, dashboard) : undefined;
};

export const isDisplayerProps = (
  widget: WidgetType['props']
): widget is WidgetParamsFromName<'displayer'> => {
  return 'displayer_id' in widget;
};

// Not to be exported: does not enforce consistency between widgets and layouts
const removeWidgetAtKey = (
  widgetId: string,
  dashboard: DashboardGridType
): DashboardGridType => {
  const newWidgetEntries = R.filterWithKeys(
    (k, _) => k !== widgetId,
    dashboard.widgets
  );
  return {
    ...dashboard,
    widgets: newWidgetEntries,
  };
};

// Not to be exported: does not enforce consistency between widgets and layouts
const removeLayoutByWidgetId = (
  widgetId: string,
  dashboard: DashboardGridType
): DashboardGridType => {
  const newLayouts = dashboard.layouts['lg']!.filter((l) => l.i !== widgetId);
  return {
    ...dashboard,
    layouts: { ...dashboard.layouts, lg: newLayouts },
  };
};

// Not to be exported: does not enforce consistency between widgets and displayers
const removeWidgetAndLayoutByElementId = (
  elementId: number,
  dashboard: DashboardGridType
): DashboardGridType => {
  const widgetKey = findWidgetKeyByElementId(elementId, dashboard);
  if (!widgetKey) {
    return dashboard;
  }
  const updatedDashboard = removeWidgetAtKey(widgetKey, dashboard);
  return removeLayoutByWidgetId(widgetKey, updatedDashboard);
};

export const getNextAvailableDisplayerKey = (
  dashboardSlice: DashboardStoreType
): number => {
  const keys = getReferencedDisplayerKeys(dashboardSlice);
  return keys.length === 0 ? 1 : Math.max(...keys) + 1;
};

export const getReferencedDisplayerKeys = (
  dashboardSlice: DashboardStoreType
): number[] => {
  const allDashboards = getDashboards('grids', dashboardSlice).concat(
    getDashboards('reports', dashboardSlice)
  );
  return allDashboards
    .flatMap(getWidgets)
    .map((widget) => widget.props)
    .filter(isDisplayerProps)
    .map((props) => props.displayer_id)
    .filter((id) => id !== undefined);
};

// -- Store

export const removeWidgetSafely = (
  elementId: number,
  gridType: GridType,
  store: DashboardLibraryState
): DashboardLibraryState => {
  const dashboardSlice = store.dashboard;
  const widgetToBeRemoved = findWidgetByElementIdInSlice(
    elementId,
    gridType,
    dashboardSlice
  );
  if (!widgetToBeRemoved) {
    return store;
  }
  const updatedDashboardSlice = updateAllDashboards(
    gridType,
    (dashboard) => removeWidgetAndLayoutByElementId(elementId, dashboard),
    dashboardSlice
  );
  return replaceDashboardSlice(updatedDashboardSlice, store);
};

const makeMapSources = (element: Map) => {
  // FIXME: ds is not used because no filtering is allowed by the dataSource structure
  return element.data_source.map(
    (ds) =>
      ({
        entity: `${element.properties.coordinate_source}s`,
        extractionStrategy: 'default',
        markerType: 'marker',
        ids: [ds.site_id],
      } as Datasource)
  );
};

const dashboardElementTypeToWidgetType = (
  displayerId: number,
  grid_type: 'grids' | 'reports',
  element: ApiDashboardElement,
  nonRemovable: boolean
): WidgetType => {
  switch (element.element_type) {
    case 'plot':
      if (element.properties.state_type === 'unconfigured_plot') {
        return makeWidgetRepresentation(
          element.id,
          element,
          'empty-initial-plot',
          {
            non_removable: nonRemovable,
          }
        );
      }
      return makeWidgetRepresentation(element.id, element, 'displayer', {
        displayer_id: displayerId,
        elementData: element,
        non_removable: nonRemovable,
      });
    case 'table':
      if (element.properties.state_type === 'unconfigured_table') {
        return makeWidgetRepresentation(
          element.id,
          element,
          'empty-initial-table',
          {
            non_removable: nonRemovable,
          }
        );
      }
      return makeWidgetRepresentation(element.id, element, 'displayer', {
        displayer_id: displayerId,
        elementData: element,
        non_removable: nonRemovable,
      });
    case 'tablebycampaign':
      if (!isConfiguredTableByCampaign(element)) {
        return makeWidgetRepresentation(
          element.id,
          element,
          'empty-initial-table',
          {
            non_removable: nonRemovable,
          }
        );
      }

      return makeWidgetRepresentation(element.id, element, 'displayer', {
        displayer_id: displayerId,
        elementData: element,
        non_removable: nonRemovable,
      });
    case 'text':
      if (!isConfiguredText(element)) {
        return makeWidgetRepresentation(
          element.id,
          element,
          'empty-initial-resource-widget',
          {
            non_removable: nonRemovable,
          }
        );
      }
      return makeWidgetRepresentation(element.id, element, 'text-input', {
        name: element.name,
        value: element.properties.value ?? '',
        non_removable: nonRemovable,
        gridType: grid_type,
      });
    case 'map':
      return makeWidgetRepresentation(element.id, element, 'mapWidget', {
        name: element.name,
        dataSource: makeMapSources(element),
        non_removable: nonRemovable,
        gridType: grid_type,
      });
    case 'textfromentityproperty':
      return makeWidgetRepresentation(
        element.id,
        element,
        'text-from-external',
        {
          name: element.name,
          table: `${element.properties.entity_type}s`,
          row_id: element.properties.entity_id,
          property: element.properties.entity_property,
          non_removable: nonRemovable,
          gridType: grid_type,
        }
      );
    case 'image':
      return makeWidgetRepresentation(element.id, element, 'imageWidget', {
        name: element.name,
        resource_id: (element.resource_ids ?? [])[0],
        project_id: element.data_source[0]?.project_id ?? 0,
        site_id: element.data_source[0]?.site_id ?? 0,
        non_removable: nonRemovable,
        grid_type,
      });
    case 'remotewidget':
      return makeWidgetRepresentation(element.id, element, 'remote-widget', {
        name: element.name,
        url: element.properties.url!,
        is_editable: true,
        specificWidgetType: element.properties.specific_widget_type,
        additionalProps: element.properties.additional_properties as Record<
          string,
          JSONSerializable
        >,
        non_removable: nonRemovable,
      });
    case 'imagegallery':
      return makeWidgetRepresentation(
        element.id,
        element,
        'image-gallery-widget',
        {
          name: element.name,
          resource_id: (element.resource_ids ?? [])[0],
          project_id: element.data_source[0]?.project_id ?? 0,
          site_id: element.data_source[0]?.site_id ?? 0,
          grid_type,
          tags: element.properties.tags,
          non_removable: nonRemovable,
        }
      );
    default:
      throw new Error(`Unsupported element type: ${element.element_type}`);
  }
};

interface addWidgetSafelyDependencies {
  sourceLabelMap: SourceMap;
  w: number;
  h: number;
  startDate: Date;
  endDate: Date;
}

interface PositioningStrategy {
  posistioningStrategy?: 'default' | 'bottom';
}

type WidgetDefaultSize = {
  w: number;
  h: number;
};

type AddWidgetResult = {
  store: DashboardLibraryState;
  added: boolean;
};

const readRequestedLayoutFromElement = (
  element: Exclude<ApiDashboardElement, Collection>,
  defaultSize: WidgetDefaultSize
): WidgetSlot => ({
  x: element.properties.x ?? 0,
  y: element.properties.y ?? 0,
  w: element.properties.w ?? defaultSize.w,
  h: element.properties.h ?? defaultSize.h,
});

const readExistingWidgetLayouts = (
  dashboard: DashboardGridType
): WidgetSlot[] => {
  return (dashboard.layouts.lg ?? []).map(({ x, y, w, h }) => ({ x, y, w, h }));
};

/**
 * Builds the grid layout for a widget, using slot settings on svg-constrained dashboards.
 */
const buildWidgetLayoutForDashboard = (
  dashboard: DashboardGridType,
  widgetKey: string,
  element: Exclude<ApiDashboardElement, Collection>,
  defaultSize: WidgetDefaultSize,
  positioningStrategy: 'default' | 'bottom'
): Layout | null => {
  if (dashboard.additionalProperties?.layoutType === 'svg-constrained') {
    const { widgetSlot } = dashboard.additionalProperties;
    const occupiedRanks = getOccupiedSlotRanks(
      widgetSlot,
      readExistingWidgetLayouts(dashboard)
    );
    const requestedLayout = readRequestedLayoutFromElement(
      element,
      defaultSize
    );
    const placement = resolveWidgetSlotPlacement(
      { elementId: element.id, requestedLayout },
      widgetSlot,
      occupiedRanks
    );

    if (!hasAssignedWidgetSlot(placement)) {
      return null;
    }

    return {
      i: widgetKey,
      ...placement.assignedSlot,
    };
  }

  return {
    i: widgetKey,
    x: element.properties.x || 0,
    y:
      positioningStrategy === 'default'
        ? element.properties.y || 0
        : Number.POSITIVE_INFINITY,
    w: element.properties.w || defaultSize.w,
    h: element.properties.h || defaultSize.h,
  };
};

type WidgetCreationDependencies = addWidgetSafelyDependencies &
  PositioningStrategy;

interface AddWidgetSafelySignatures {
  (
    dep: WidgetCreationDependencies,
    dashboardId: number,
    gridType: 'grids',
    element: Exclude<ApiDashboardElement, Collection>,
    store: DashboardLibraryState,
    preassignedLayout?: Layout
  ): DashboardLibraryState;
  (
    dep: WidgetCreationDependencies & ReportDashboardDependencies,
    dashboardId: number,
    gridType: 'reports',
    element: Exclude<ApiDashboardElement, Collection>,
    store: DashboardLibraryState,
    preassignedLayout?: Layout
  ): DashboardLibraryState;
}

const addWidgetSafelyToUserDashboard = (
  dep: WidgetCreationDependencies,
  dashboardId: number,
  nextDisplayerId: number,
  element: Exclude<ApiDashboardElement, Collection>,
  store: DashboardLibraryState,
  preassignedLayout?: Layout
): AddWidgetResult => {
  const positioningStrategy = dep.posistioningStrategy ?? 'default';
  const dashboardSlice = store.dashboard;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dashboard = getDashboardAtKey(dashboardId, 'grids', dashboardSlice)!;
  const nextWidgetId = getNextAvailableWidgetKey(dashboard);
  const newLayout =
    preassignedLayout ??
    buildWidgetLayoutForDashboard(
      dashboard,
      nextWidgetId,
      element,
      { w: dep.w, h: dep.h },
      positioningStrategy
    );

  if (!newLayout) {
    return { store, added: false };
  }

  const layoutWithWidgetKey = { ...newLayout, i: nextWidgetId };
  const newWidget = dashboardElementTypeToWidgetType(
    nextDisplayerId,
    'grids',
    element,
    false
  );
  const updatedDashboard = addWidgetAndLayout(
    newWidget,
    layoutWithWidgetKey,
    dashboard
  );
  const updatedSlice = replaceDashboardAtKey(
    dashboardId,
    'grids',
    updatedDashboard,
    store.dashboard
  );
  return {
    store: replaceDashboardSlice(updatedSlice, store),
    added: true,
  };
};

// TODO: create report-structure-operation.ts to generate ApiReturnDashboard from ReportStructure

const addWidgetSafelyToReportDashboard = (
  dep: WidgetCreationDependencies & ReportDashboardDependencies,
  dashboardId: number,
  nextDisplayerId: number,
  element: Exclude<ApiDashboardElement, Collection>,
  store: DashboardLibraryState,
  preassignedLayout?: Layout
): AddWidgetResult => {
  const positioningStrategy = dep.posistioningStrategy ?? 'default';
  const dashboardSlice = store.dashboard;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dashboard = getDashboardAtKey(dashboardId, 'reports', dashboardSlice)!;
  const nonRemovable = Boolean(dep.elementNameToVariableName[element.name]);
  const nextWidgetId =
    dep.elementNameToVariableName[element.name] ??
    getNextAvailableWidgetKey(dashboard);
  const slotBasedLayout =
    preassignedLayout ??
    buildWidgetLayoutForDashboard(
      dashboard,
      nextWidgetId,
      element,
      { w: dep.w, h: dep.h },
      positioningStrategy
    );
  const newLayout =
    dashboard.additionalProperties?.layoutType === 'svg-constrained'
      ? slotBasedLayout
      : dep.layouts.find(
          ({ i }: Layout) => i === dep.elementNameToVariableName[element.name]
        ) ?? slotBasedLayout;

  if (!newLayout) {
    return { store, added: false };
  }

  const layoutWithWidgetKey = { ...newLayout, i: nextWidgetId };
  const newWidget = dashboardElementTypeToWidgetType(
    nextDisplayerId,
    'reports',
    element,
    nonRemovable
  );
  const updatedDashboard = addWidgetAndLayout(
    newWidget,
    layoutWithWidgetKey,
    dashboard
  );
  const updatedSlice = replaceDashboardAtKey(
    dashboardId,
    'reports',
    updatedDashboard,
    store.dashboard
  );
  return {
    store: replaceDashboardSlice(updatedSlice, store),
    added: true,
  };
};

export const addWidgetSafely: AddWidgetSafelySignatures = (
  dep,
  dashboardId,
  gridType,
  element,
  store,
  preassignedLayout
): DashboardLibraryState => {
  const nextDisplayerId = getNextAvailableDisplayerKey(store.dashboard);
  const addWidgetResult =
    gridType === 'grids'
      ? addWidgetSafelyToUserDashboard(
          dep,
          dashboardId,
          nextDisplayerId,
          element,
          store,
          preassignedLayout
        )
      : addWidgetSafelyToReportDashboard(
          dep as WidgetCreationDependencies & ReportDashboardDependencies,
          dashboardId,
          nextDisplayerId,
          element,
          store,
          preassignedLayout
        );

  if (!addWidgetResult.added) {
    return store;
  }

  return addWidgetResult.store;
};

const flattenElementsHelper = (
  acc: Exclude<ApiDashboardElement, Collection>[],
  element: ApiDashboardElement
): typeof acc => {
  if (element.element_type === 'collection') {
    const elements = element.elements.flatMap((subElement) =>
      flattenElementsHelper([], subElement)
    );
    return acc.concat(elements);
  }
  return acc.concat(element);
};

const flattenElements = (
  elements: ApiDashboardElement[]
): Exclude<ApiDashboardElement, Collection>[] => {
  return elements.reduce(
    (acc, element) => flattenElementsHelper(acc, element),
    [] as Exclude<ApiDashboardElement, Collection>[]
  );
};

const buildSvgConstrainedPlacementRequests = (
  elements: Exclude<ApiDashboardElement, Collection>[],
  defaultSize: WidgetDefaultSize
) => {
  return elements.map((element) => ({
    elementId: element.id,
    requestedLayout: readRequestedLayoutFromElement(element, defaultSize),
  }));
};

const addResolvedSvgConstrainedElements = (
  dep: WidgetCreationDependencies,
  dashboardId: number,
  elements: Exclude<ApiDashboardElement, Collection>[],
  widgetSlot: WidgetSlot[],
  store: DashboardLibraryState
): DashboardLibraryState => {
  const placementRequests = buildSvgConstrainedPlacementRequests(elements, {
    w: dep.w,
    h: dep.h,
  });
  const resolvedPlacements = resolveSlotConflictsByGeometry(
    placementRequests,
    widgetSlot
  );
  const elementsById = new Map(
    elements.map((element) => [element.id, element])
  );

  return resolvedPlacements
    .filter(hasAssignedWidgetSlot)
    .reduce((acc, placement) => {
      const element = elementsById.get(placement.elementId);
      if (!element) {
        return acc;
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const dashboard = getDashboardAtKey(
        dashboardId,
        'grids',
        getDashboardSlice(acc)
      )!;
      const preassignedLayout: Layout = {
        i: getNextAvailableWidgetKey(dashboard),
        ...placement.assignedSlot,
      };

      return addWidgetSafely(
        dep,
        dashboardId,
        'grids',
        element,
        acc,
        preassignedLayout
      );
    }, store);
};

const addResolvedSvgConstrainedElementsToReportDashboard = (
  dep: WidgetCreationDependencies & ReportDashboardDependencies,
  dashboardId: number,
  elements: Exclude<ApiDashboardElement, Collection>[],
  widgetSlot: WidgetSlot[],
  store: DashboardLibraryState
): DashboardLibraryState => {
  const placementRequests = buildSvgConstrainedPlacementRequests(elements, {
    w: dep.w,
    h: dep.h,
  });
  const resolvedPlacements = resolveSlotConflictsByGeometry(
    placementRequests,
    widgetSlot
  );
  const elementsById = new Map(
    elements.map((element) => [element.id, element])
  );

  return resolvedPlacements
    .filter(hasAssignedWidgetSlot)
    .reduce((acc, placement) => {
      const element = elementsById.get(placement.elementId);
      if (!element) {
        return acc;
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const dashboard = getDashboardAtKey(
        dashboardId,
        'reports',
        getDashboardSlice(acc)
      )!;
      const widgetKey =
        dep.elementNameToVariableName[element.name] ??
        getNextAvailableWidgetKey(dashboard);
      const preassignedLayout: Layout = {
        i: widgetKey,
        ...placement.assignedSlot,
      };

      return addWidgetSafely(
        dep,
        dashboardId,
        'reports',
        element,
        acc,
        preassignedLayout
      );
    }, store);
};

const addUserDashboardElementsSafely = (
  dep: WidgetCreationDependencies,
  dashboardId: number,
  elements: Exclude<ApiDashboardElement, Collection>[],
  additionalProperties: DashboardLayoutProperties | undefined,
  store: DashboardLibraryState
): DashboardLibraryState => {
  if (additionalProperties?.layoutType === 'svg-constrained') {
    return addResolvedSvgConstrainedElements(
      dep,
      dashboardId,
      elements,
      additionalProperties.widgetSlot,
      store
    );
  }

  return elements.reduce(
    (acc, element) => addWidgetSafely(dep, dashboardId, 'grids', element, acc),
    store
  );
};

const addReportDashboardElementsSafely = (
  dep: WidgetCreationDependencies & ReportDashboardDependencies,
  dashboardId: number,
  elements: Exclude<ApiDashboardElement, Collection>[],
  additionalProperties: DashboardLayoutProperties | undefined,
  store: DashboardLibraryState
): DashboardLibraryState => {
  if (additionalProperties?.layoutType === 'svg-constrained') {
    return addResolvedSvgConstrainedElementsToReportDashboard(
      dep,
      dashboardId,
      elements,
      additionalProperties.widgetSlot,
      store
    );
  }

  return elements.reduce(
    (acc, element) =>
      addWidgetSafely(dep, dashboardId, 'reports', element, acc),
    store
  );
};

interface AddDashboardSafelySignatures {
  (
    dep: WidgetCreationDependencies,
    gridType: 'grids',
    dashboardRepresentation: ApiDashboardParametersType,
    store: DashboardLibraryState
  ): DashboardLibraryState;
  (
    dep: WidgetCreationDependencies & ReportDashboardDependencies,
    gridType: 'reports',
    dashboardRepresentation: ApiDashboardParametersType,
    store: DashboardLibraryState
  ): DashboardLibraryState;
}

const addUserDashboardSafely = (
  dep: WidgetCreationDependencies,
  dashboardRepresentation: ApiDashboardParametersType,
  store: DashboardLibraryState
): DashboardLibraryState => {
  const dashboardSlice = store.dashboard;
  const dashboardId = dashboardRepresentation.id;
  const publicUrl = dashboardRepresentation.sharing_config?.url;
  if (!dashboardId) {
    throw new Error('Cannot proceed: undefined dashboard id');
  }
  const newDashboard = createEmptyDashboard(
    dashboardRepresentation.name,
    publicUrl,
    normalizeAdditionalProperties(dashboardRepresentation.additional_properties)
  );
  const updatedDashboardSlice = addDashboardAtKey(
    dashboardId,
    'grids',
    newDashboard,
    dashboardSlice
  );
  const updatedStore = replaceDashboardSlice(updatedDashboardSlice, store);
  const additionalProperties = normalizeAdditionalProperties(
    dashboardRepresentation.additional_properties
  );
  return addUserDashboardElementsSafely(
    dep,
    dashboardId,
    flattenElements(dashboardRepresentation.elements),
    additionalProperties,
    updatedStore
  );
};

// FIXME: should add the report structure

const addReportDashboardSafely = (
  dep: WidgetCreationDependencies & ReportDashboardDependencies,
  dashboardRepresentation: ApiDashboardParametersType,
  store: DashboardLibraryState
): DashboardLibraryState => {
  const dashboardSlice = store.dashboard;
  const dashboardId = dashboardRepresentation.id;
  if (!dashboardId) {
    throw new Error('Cannot proceed: undefined dashboard id');
  }
  const newDashboard = createEmptyDashboard(
    dashboardRepresentation.name,
    undefined,
    normalizeAdditionalProperties(dashboardRepresentation.additional_properties)
  );
  const newDashboardWithParams = setDashboardParams(
    dep.reportStructure,
    newDashboard
  );
  const updatedDashboardSlice = addDashboardAtKey(
    dashboardId,
    'reports',
    newDashboardWithParams,
    dashboardSlice
  );
  const updatedStore = replaceDashboardSlice(updatedDashboardSlice, store);
  const additionalProperties = normalizeAdditionalProperties(
    dashboardRepresentation.additional_properties
  );
  return addReportDashboardElementsSafely(
    dep,
    dashboardId,
    flattenElements(dashboardRepresentation.elements),
    additionalProperties,
    updatedStore
  );
};

/**
 * Hydrates a dashboard representation into Redux widgets.
 *
 */
export const addDashboardSafely: AddDashboardSafelySignatures = (
  dep,
  gridType,
  dashboardRepresentation,
  store
): DashboardLibraryState => {
  switch (gridType) {
    case 'grids':
      return addUserDashboardSafely(dep, dashboardRepresentation, store);
    case 'reports':
      return addReportDashboardSafely(
        dep as WidgetCreationDependencies & ReportDashboardDependencies,
        dashboardRepresentation,
        store
      );
  }
};

export const addEmptyDashboardSafely = (
  gridType: GridType,
  dashboardName: string,
  store: DashboardLibraryState
): DashboardLibraryState => {
  const dashboardSlice = store.dashboard;
  const newDashboard = createEmptyDashboard(dashboardName);
  const updatedDashboardSlice = addDashboard(
    gridType,
    newDashboard,
    dashboardSlice
  );
  return replaceDashboardSlice(updatedDashboardSlice, store);
};

export const removeDashboardSafely = (
  dashboardId: number,
  gridType: GridType,
  store: DashboardLibraryState
): DashboardLibraryState => {
  // retreive dashboard
  const dashboardSlice = store.dashboard;
  const dashboard = getDashboardAtKey(dashboardId, gridType, dashboardSlice);
  if (!dashboard) {
    return store;
  }
  // remove all of its widget safely
  const widgets = getWidgets(dashboard);
  const elementIds = widgets.map((widget) => widget.dashboardElementId);
  const storeWithoutWidgets = elementIds.reduce(
    (acc, elementId) => removeWidgetSafely(elementId, gridType, acc),
    store
  );
  // remove the dashboard
  const updatedDashboardSlice = getDashboardSlice(storeWithoutWidgets);
  const updatedDashboards = removeDashboardAtKey(
    dashboardId,
    gridType,
    updatedDashboardSlice
  );
  return replaceDashboardSlice(updatedDashboards, storeWithoutWidgets);
};

export const getDashboardElementIdMappingFromDashboard = (
  dashboardId: number,
  gridType: GridType,
  store: DashboardLibraryState
) => {
  return getWidgetKeyToDashboardElementIdMappingAtKey(
    dashboardId,
    gridType,
    store.dashboard
  );
};

export const getWidgetFromIdMapping = (
  widgetId: string,
  gridType: GridType,
  dashboardId: number,
  dashboardSlice: DashboardStoreType
): WidgetType | undefined => {
  const dashboard = getDashboardAtKey(dashboardId, gridType, dashboardSlice);
  if (dashboard === undefined) return undefined;
  const widget = findWidgetByElementId(Number.parseInt(widgetId), dashboard);
  return widget;
};
